import { useCallback, useEffect, useId, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  List,
  ListItem,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConfirmIcon from '@mui/icons-material/CheckCircleOutline';
import FalsePositiveIcon from '@mui/icons-material/ThumbDownAltOutlined';
import GitHubIcon from '@mui/icons-material/GitHub';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ResolveIcon from '@mui/icons-material/TaskAltOutlined';
import WontFixIcon from '@mui/icons-material/BlockOutlined';
import ReopenIcon from '@mui/icons-material/ReplayOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';
import {
  findingsService,
  type FalsePositiveReason,
  type WontFixReason,
  type FindingInstanceDetail,
  type FindingSiblings,
} from '@services/findingsService';
import { integrationService } from '@services/integrationService';
import AddToRemediationPlanDialog from '@components/remediation/AddToRemediationPlanDialog';
import AiFixSuggestion from '@components/findings/AiFixSuggestion';
import { RuleTitle } from '@components/findings/RuleTitle';
import { stripCodeSpans } from '@utils/codeSpans';
import { severityChipColor } from '@utils/findingSeverity';
import { canTriageFindings } from '@utils/findingTriage';
import { apiErrorCode, toDisplayError } from '../utils/apiError';
import CodeBlock from '@components/common/CodeBlock';

/**
 * REV-509: the two dispositions have separate vocabularies. A false positive
 * says the tool was WRONG; won't fix concedes it was RIGHT but declines to act.
 * These mirror src/constants/dispositionReasons.js in revenant-api — the API
 * rejects anything outside the matching list with a 400.
 */
const FALSE_POSITIVE_REASONS: FalsePositiveReason[] = [
  'incorrect_detection',
  'incorrect_wcag_mapping',
  'context_specific',
  'testing_artifact',
  'other',
];

const WONT_FIX_REASONS: WontFixReason[] = [
  'by_design',
  'third_party',
  'not_applicable',
  'duplicate',
  'other',
];

/**
 * REV-220: statuses that represent a closed/dispositioned finding.
 *
 * The finding statuses and what the API lets a user do from each, mirrored
 * from revenant-api src/constants/dispositionReasons.js and
 * src/services/feedbackService.js (REV-939):
 *
 *   confirm         refused (409) from a CLOSED status; offered from open and
 *                   in_progress only — confirming a confirmed finding changes
 *                   nothing, so it is not offered twice.
 *   false positive  refused (409) from acknowledged; not offered once closed.
 *   won't fix       not offered once closed.
 *   resolve         not offered once closed.
 *   reopen          accepted only from REOPENABLE: the closed statuses and
 *                   acknowledged. From acknowledged it withdraws the
 *                   confirmation, so it is labelled that way.
 */
const CLOSED_STATUSES = ['resolved', 'false_positive', 'wont_fix'];

/** The stored status of a confirmed finding; the page labels it "Confirmed". */
const CONFIRMED_STATUS = 'acknowledged';

function statusChipColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status === 'resolved') return 'success';
  if (status === 'false_positive') return 'default';
  if (status === 'wont_fix') return 'warning';
  if (status === 'acknowledged' || status === 'in_progress') return 'info';
  return 'error'; // open
}

/**
 * REV-134: finding detail page. Shown when a user drills into a row from
 * the findings list (REV-133) or the context detail page. Renders the
 * full rule description, WCAG reference, failing/passing code samples,
 * the element markup, remediation guidance, and feedback buttons wired
 * to the existing /findings/:id/confirm and /false-positive endpoints.
 */
export default function FindingDetailPage() {
  const { t } = useTranslation();
  const { slug, findingId } = useParams<{ slug: string; findingId: string }>();

  const [orgId, setOrgId] = useState<string | null>(null);
  // REV-949: the caller's role decides whether the status actions are offered.
  // Null until the organization has loaded, which offers none (fail closed).
  const [orgRole, setOrgRole] = useState<string | null>(null);
  const triageNoteId = useId();
  const [finding, setFinding] = useState<FindingInstanceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Add-to-remediation-plan dialog (REV-466)
  const [addToPlanOpen, setAddToPlanOpen] = useState(false);

  // Overflow menu for secondary actions (REV-509)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);

  // False-positive dialog. fpError is dialog-scoped: rendering a validation
  // error into the page-level Alert would put it in the dialog's aria-hidden
  // background, where a screen reader user would never encounter it.
  const [fpOpen, setFpOpen] = useState(false);
  const [fpReason, setFpReason] = useState<FalsePositiveReason>('incorrect_detection');
  const [fpNotes, setFpNotes] = useState('');
  const [fpError, setFpError] = useState<string | null>(null);

  // Won't-fix dialog (REV-220) — mirrors the false-positive dialog, including
  // a dialog-scoped error (REV-722): a failure leaves the dialog open, and the
  // page-level Alert behind it is covered and aria-hidden.
  const [wfOpen, setWfOpen] = useState(false);
  const [wfReason, setWfReason] = useState<WontFixReason>('by_design');
  const [wfNotes, setWfNotes] = useState('');
  const [wfError, setWfError] = useState<string | null>(null);

  // Resolve dialog (REV-220) — optional notes, no reason. Its error is
  // dialog-scoped for the same reason (REV-722).
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Export to GitHub (REV-478)
  const [siblings, setSiblings] = useState<FindingSiblings | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportedUrl, setExportedUrl] = useState<string | null>(null);
  // REV-939: the no-connection refusal names the Integrations settings, so it
  // carries a real link to them — which a plain error string cannot.
  const [exportNoConnection, setExportNoConnection] = useState(false);

  // Every error on the page clears on the next action or load. The
  // no-connection guidance is its own state, so clearing only `error` left it
  // up after a successful action and on moving to a sibling finding (the same
  // page with a new id, which reloads) — REV-939 review.
  const clearErrors = useCallback(() => {
    setError(null);
    setExportNoConnection(false);
  }, []);

  const load = useCallback(async () => {
    if (!slug || !findingId) return;
    setLoading(true);
    clearErrors();
    // Clear stale siblings up front. Relying on the .catch() below to null them
    // only works on a FIRST load; navigating from one finding to another whose
    // siblings request then fails would otherwise leave the previous finding's
    // panel on screen, attributing its elements to the wrong rule.
    setSiblings(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
      setOrgRole(org.role ?? null);
      const data = await findingsService.getInstance(org.id, findingId);
      setFinding(data);
      // REV-539: supplementary, and deliberately not awaited into the same
      // try/catch outcome — a siblings failure must not take down the finding
      // the user actually navigated to.
      findingsService
        .getSiblings(org.id, findingId)
        .then(setSiblings)
        .catch(() => setSiblings(null));
    } catch (err) {
      setError(
        toDisplayError(err, t('findingDetail.errors.fetchFailed', 'Failed to load finding'))
      );
    } finally {
      setLoading(false);
    }
  }, [slug, findingId, t, clearErrors]);

  useEffect(() => {
    void load();
  }, [load]);

  // REV-949: a viewer may read a finding but not change its status. The API
  // refuses it too (403); this keeps the page from offering what it refuses.
  const canTriage = canTriageFindings(orgRole);

  const handleConfirm = async () => {
    if (!orgId || !findingId || !canTriage) return;
    setSubmitting(true);
    clearErrors();
    try {
      await findingsService.confirmFinding(orgId, findingId);
      setToast(t('findingDetail.confirmed', 'Finding confirmed.'));
      await load();
    } catch (err) {
      setError(
        toDisplayError(err, t('findingDetail.errors.confirmFailed', 'Failed to confirm finding'))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFpSubmit = async () => {
    if (!orgId || !findingId || !canTriage) return;

    // REV-509: a false-positive claim asserts the tool was wrong, so it must be
    // justified. Validated on submit — the button is never pre-disabled, so the
    // user can always click and be told what is missing.
    const trimmedNotes = fpNotes.trim();
    if (!trimmedNotes) {
      setFpError(
        t(
          'findingDetail.fpDialog.notesRequired',
          'Notes are required. Explain why this finding is incorrect.'
        )
      );
      return;
    }

    setSubmitting(true);
    clearErrors();
    setFpError(null);
    try {
      await findingsService.markFalsePositive(orgId, findingId, {
        reason: fpReason,
        notes: trimmedNotes,
      });
      setFpOpen(false);
      setFpNotes('');
      setToast(t('findingDetail.markedFalsePositive', 'Finding marked as false positive.'));
      await load();
    } catch (err) {
      // The dialog stays open on failure, so the message belongs inside it —
      // the page-level Alert behind an open modal is aria-hidden.
      setFpError(toDisplayError(err, t('findingDetail.errors.fpFailed', 'Failed to mark finding')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWontFixSubmit = async () => {
    if (!orgId || !findingId || !canTriage) return;
    setSubmitting(true);
    clearErrors();
    setWfError(null);
    try {
      await findingsService.markWontFix(orgId, findingId, {
        reason: wfReason,
        notes: wfNotes.trim() || undefined,
      });
      setWfOpen(false);
      setWfNotes('');
      setToast(t('findingDetail.markedWontFix', "Finding marked as won't fix."));
      await load();
    } catch (err) {
      // The dialog stays open on failure, so the message belongs inside it.
      setWfError(
        toDisplayError(err, t('findingDetail.errors.wontFixFailed', 'Failed to mark finding'))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolveSubmit = async () => {
    if (!orgId || !findingId || !canTriage) return;
    setSubmitting(true);
    clearErrors();
    setResolveError(null);
    try {
      await findingsService.resolveFinding(orgId, findingId, resolveNotes.trim() || undefined);
      setResolveOpen(false);
      setResolveNotes('');
      setToast(t('findingDetail.markedResolved', 'Finding marked as resolved.'));
      await load();
    } catch (err) {
      // The dialog stays open on failure, so the message belongs inside it.
      setResolveError(
        toDisplayError(err, t('findingDetail.errors.resolveFailed', 'Failed to resolve finding'))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!orgId || !findingId || !canTriage) return;
    setSubmitting(true);
    clearErrors();
    try {
      const withdrawing = finding?.status === CONFIRMED_STATUS;
      await findingsService.reopenFinding(orgId, findingId);
      setToast(
        withdrawing
          ? t('findingDetail.confirmationWithdrawn', 'Confirmation withdrawn.')
          : t('findingDetail.reopened', 'Finding reopened.')
      );
      await load();
    } catch (err) {
      setError(
        toDisplayError(err, t('findingDetail.errors.reopenFailed', 'Failed to reopen finding'))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    if (!orgId || !findingId || !canTriage) return;
    setExporting(true);
    clearErrors();
    try {
      const result = await integrationService.exportFinding(orgId, findingId);
      setExportedUrl(result.external_issue_url);
      setToast(t('findingDetail.export.success', 'Exported to GitHub as a tracked issue.'));
    } catch (err) {
      // An already-exported finding comes back as a 409 carrying the existing
      // issue URL — treat that as success rather than an error (REV-478).
      const alreadyExported = (
        err as { response?: { status?: number; data?: { data?: { external_issue_url?: string } } } }
      )?.response;
      if (alreadyExported?.status === 409 && alreadyExported.data?.data?.external_issue_url) {
        setExportedUrl(alreadyExported.data.data.external_issue_url);
        setToast(t('findingDetail.export.already', 'This finding is already tracked on GitHub.'));
      } else {
        // A missing connection needs actionable guidance, not the raw message —
        // and a link to where the connection is made (REV-939).
        if (apiErrorCode(err) === 'no_connection') {
          setExportNoConnection(true);
        } else {
          setError(
            toDisplayError(err, t('findingDetail.export.failed', 'Failed to export the finding'))
          );
        }
      }
    } finally {
      setExporting(false);
    }
  };

  if (loading && !finding) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !finding) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        {/* REV-732: goes to a URL, so it is a link. */}
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to={`/orgs/${slug}/findings`}
        >
          {t('findingDetail.backToList', 'Back to findings')}
        </Button>
      </Box>
    );
  }

  if (!finding) return null;

  const def = finding.definition;
  const criteriaLabel = (def?.wcag_criteria || []).join(', ') || '—';
  const issueUrl = exportedUrl || finding.external_issue_url || null;
  const isClosed = CLOSED_STATUSES.includes(finding.status);
  // REV-509: confirming asserts the finding is real, which is incompatible with
  // asserting it is a false positive — the API returns 409, so don't offer it.
  // Reopen withdraws the confirmation, which is why it is offered here too.
  const isConfirmed = finding.status === CONFIRMED_STATUS;
  const canMarkFalsePositive = !isClosed && !isConfirmed;
  /**
   * REV-951: the menu's writes all need member or above — adding to a
   * remediation plan and exporting to GitHub as much as the status changes
   * (requireOrgRole('member') on both routes) — so a viewer is offered none of
   * them. Only the link to an existing issue is a read. A menu left with
   * nothing in it is not rendered at all: a trigger opening an empty menu
   * would be a control that leads nowhere.
   */
  const hasMenuItems = canTriage || !!issueUrl;
  /**
   * REV-949: for a viewer the status buttons stay, so the reader learns the
   * actions exist and why they are unavailable: aria-disabled, never
   * `disabled` (which drops a control from the tab order and puts the
   * explanation out of reach), described by the note below the header, and
   * styled to look unavailable, which aria-disabled alone does not do in MUI
   * (REV-681). Their handlers do nothing for a viewer.
   */
  const unavailable = canTriage
    ? {}
    : {
        'aria-disabled': true as const,
        'aria-describedby': triageNoteId,
        disableRipple: true,
        sx: {
          '&[aria-disabled="true"]': {
            bgcolor: 'action.disabledBackground',
            color: 'text.secondary',
            boxShadow: 'none',
            cursor: 'not-allowed',
            '&:hover': { bgcolor: 'action.disabledBackground', boxShadow: 'none' },
          },
        },
      };
  const statusLabel = t(
    `findingDetail.statuses.${finding.status}`,
    finding.status.replace(/_/g, ' ')
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* REV-732: goes to a URL, so it is a link. */}
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to={`/orgs/${slug}/findings`}
        sx={{ mb: 2 }}
      >
        {t('findingDetail.backToList', 'Back to findings')}
      </Button>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            <RuleTitle
              text={def?.name || finding.element_selector || t('findingDetail.untitled', 'Finding')}
            />
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={severityChipColor(finding.severity)}
              label={t(`findingDetail.severity.${finding.severity}`, finding.severity)}
            />
            {def?.wcag_level && (
              <Chip
                size="small"
                variant="outlined"
                label={t('findingDetail.wcagLevel', {
                  defaultValue: 'WCAG {{level}}',
                  level: def.wcag_level,
                })}
              />
            )}
            {def?.category && <Chip size="small" variant="outlined" label={def.category} />}
            {def?.source && (
              <Chip
                size="small"
                variant="outlined"
                label={t(`findingDetail.source.${def.source}`, def.source)}
              />
            )}
            <Chip
              size="small"
              color={statusChipColor(finding.status)}
              // No aria-label here: a non-clickable MUI Chip is a plain <div>
              // (role=generic), and ARIA prohibits naming a generic element.
              // The `label` prop already renders this exact string as visible
              // text, so the attribute was redundant as well as invalid.
              // Caught by the dogfooded afixt-engine gate as ARIA-40.
              label={t('findingDetail.statusLabel', {
                defaultValue: 'Status: {{status}}',
                status: statusLabel,
              })}
            />
          </Stack>
        </Box>
        {/*
          REV-509: only the primary dispositions stay as buttons. Six competing
          buttons consumed the whole header and gave equal visual weight to
          rare, hard-to-reverse actions; the rest now live in an overflow menu.
        */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-start">
          {!isClosed && (
            <>
              {!isConfirmed && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ConfirmIcon />}
                  onClick={handleConfirm}
                  disabled={submitting}
                  {...unavailable}
                >
                  {t('findingDetail.actions.confirm', 'Confirm')}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<ResolveIcon />}
                onClick={() => {
                  if (!canTriage) return;
                  setResolveError(null);
                  setResolveOpen(true);
                }}
                disabled={submitting}
                {...unavailable}
              >
                {t('findingDetail.actions.resolve', 'Resolve')}
              </Button>
            </>
          )}
          {(isClosed || isConfirmed) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ReopenIcon />}
              onClick={handleReopen}
              disabled={submitting}
              {...unavailable}
            >
              {isConfirmed
                ? t('findingDetail.actions.withdrawConfirmation', 'Withdraw confirmation')
                : t('findingDetail.actions.reopen', 'Reopen')}
            </Button>
          )}

          {hasMenuItems && (
            <>
              <Tooltip title={t('findingDetail.actions.more', 'More actions')}>
                <IconButton
                  id="finding-actions-trigger"
                  aria-label={t('findingDetail.actions.more', 'More actions')}
                  aria-haspopup="true"
                  aria-expanded={menuOpen}
                  aria-controls={menuOpen ? 'finding-actions-menu' : undefined}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={closeMenu}
                // The id goes on the MenuList, not the Menu: MUI puts a Menu-level
                // `id` on the outer Popover wrapper, so aria-controls would point at
                // a div rather than the element that actually has role="menu".
                // Caught by the afixt-engine e2e gate, which unit tests missed.
                // A role="menu" with no accessible name is announced as an unnamed
                // group. aria-labelledby pointing at the trigger does NOT work here:
                // the trigger holds only an icon, so its name comes from its own
                // aria-label and the referenced element's *contents* are empty —
                // which computes to a blank name and overrides everything else.
                // Label the menu directly instead. Caught by the e2e a11y gate.
                MenuListProps={{
                  id: 'finding-actions-menu',
                  'aria-label': t('findingDetail.actions.more', 'More actions'),
                }}
                // MUI restores focus to the trigger on close by default, so a
                // keyboard user is never dropped back at the top of the document.
              >
                {canTriage && canMarkFalsePositive && (
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      setFpError(null);
                      setFpOpen(true);
                    }}
                    disabled={submitting}
                  >
                    <ListItemIcon>
                      <FalsePositiveIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {t('findingDetail.actions.falsePositive', 'False positive')}
                    </ListItemText>
                  </MenuItem>
                )}
                {canTriage && !isClosed && (
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      setWfError(null);
                      setWfOpen(true);
                    }}
                    disabled={submitting}
                  >
                    <ListItemIcon>
                      <WontFixIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('findingDetail.actions.wontFix', "Won't fix")}</ListItemText>
                  </MenuItem>
                )}
                {canTriage && (
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      setAddToPlanOpen(true);
                    }}
                    disabled={submitting}
                  >
                    <ListItemIcon>
                      <PlaylistAddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {t('findingDetail.actions.addToPlan', 'Add to remediation plan')}
                    </ListItemText>
                  </MenuItem>
                )}
                {issueUrl ? (
                  <MenuItem
                    component={Link}
                    href={issueUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={closeMenu}
                  >
                    <ListItemIcon>
                      <GitHubIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {t('findingDetail.actions.viewIssue', 'View GitHub issue')}
                    </ListItemText>
                    <OpenInNewIcon fontSize="inherit" sx={{ ml: 1 }} />
                  </MenuItem>
                ) : (
                  canTriage && (
                    <MenuItem
                      onClick={() => {
                        closeMenu();
                        void handleExport();
                      }}
                      disabled={exporting}
                    >
                      <ListItemIcon>
                        <GitHubIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        {exporting
                          ? t('findingDetail.actions.exporting', 'Exporting…')
                          : t('findingDetail.actions.exportGithub', 'Export to GitHub')}
                      </ListItemText>
                    </MenuItem>
                  )
                )}
              </Menu>
            </>
          )}
        </Stack>
      </Stack>
      {!canTriage && (
        // REV-949: why the status buttons above are unavailable. Outside them,
        // so it is not part of their accessible names, and referenced from
        // each by aria-describedby.
        <Typography id={triageNoteId} variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'findingDetail.triageUnavailable',
            'Your role can view findings but not change their status. Ask a member, manager, admin or owner to change it, or to change your role.'
          )}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {exportNoConnection && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportNoConnection(false)}>
          {/* REV-939: the settings it names are a destination, so a link. */}
          <Trans
            i18nKey="findingDetail.export.noConnection"
            defaults="No GitHub connection is configured for this organization. Connect GitHub in the organization <0>Integrations settings</0>, then try exporting again."
            components={[
              <Link component={RouterLink} to={`/orgs/${slug}/integrations`} key="integrations" />,
            ]}
          />
        </Alert>
      )}
      {toast && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setToast(null)}>
          {toast}
          {/*
            REV-509: the GitHub action moved into the overflow menu, so a freshly
            created issue would otherwise only be reachable by reopening that
            menu. Surface the link right where the user is looking instead.
          */}
          {exportedUrl && (
            <>
              {' '}
              <Link href={exportedUrl} target="_blank" rel="noreferrer">
                {t('findingDetail.actions.viewIssue', 'View GitHub issue')}
                <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
              </Link>
            </>
          )}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" component="h2" color="text.secondary">
          {t('findingDetail.sections.description', 'Description')}
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }}>
          {def?.description || t('findingDetail.noDescription', 'No description available.')}
        </Typography>
        {def?.impact && (
          <>
            <Typography variant="subtitle2" component="h3" color="text.secondary" sx={{ mt: 2 }}>
              {t('findingDetail.sections.impact', 'Impact')}
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {def.impact}
            </Typography>
          </>
        )}
        <Divider sx={{ my: 2 }} />
        {/* REV-721: the three labels below are `variant="caption"`, which MUI
            maps to a <span> — inline. WCAG criteria and Rule id put their value
            in a body2 <p>, a block, so the value dropped to its own line by
            accident of the element. Reference put its value in a <Link>, an
            inline <a>, so label and link sat on one line "smashed together".
            The layout only ever looked consistent because two of three values
            happened to be blocks.
            display="block" on each label makes it deliberate instead. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('findingDetail.sections.wcag', 'WCAG criteria')}
            </Typography>
            <Typography variant="body2">{criteriaLabel}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('findingDetail.sections.rule', 'Rule id')}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {def?.rule_id || '—'}
            </Typography>
          </Box>
          {def?.documentation_url && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('findingDetail.sections.reference', 'Reference')}
              </Typography>
              <Link
                href={def.documentation_url}
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {/* REV-508: name the document. "Open docs" on every rule told
                    the reader nothing about where the link goes; the title is
                    the reference's own markdown link text. Falls back when the
                    rule-docs catalog does not cover this rule. */}
                {def.documentation_title || t('findingDetail.openDocs', 'Open docs')}
                <OpenInNewIcon fontSize="inherit" />
              </Link>
            </Box>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" component="h2" color="text.secondary">
          {t('findingDetail.sections.where', 'Where it was found')}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>{t('findingDetail.context', 'Context')}:</strong>{' '}
            {finding.context_name || finding.context_canonical_identifier || '—'}
          </Typography>
          {finding.target_url && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>{t('findingDetail.page', 'Page')}:</strong>{' '}
              <Link href={finding.target_url} target="_blank" rel="noreferrer">
                {finding.target_url}
              </Link>
            </Typography>
          )}
          {finding.element_selector && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              <strong>{t('findingDetail.selector', 'Selector')}:</strong> {finding.element_selector}
            </Typography>
          )}
          {/* REV-1152: what the test found on THIS element, in the engine's
              words (a contrast ratio with its foreground and background
              colours, a missing id). Text, so a colour is never conveyed by
              colour alone. */}
          {finding.details && (
            <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
              <strong>{t('findingDetail.detailsLabel', 'What the test found')}:</strong>{' '}
              <span>{finding.details}</span>
            </Typography>
          )}
        </Box>
        {finding.element_html && <CodeBlock code={finding.element_html} />}
      </Paper>

      {/* REV-539: how many DISTINCT elements this rule fails on. The old
          "Occurrences" number counted how many SCANS re-detected this ONE
          element, which told a remediator nothing (REV-508 removed it).
          Rendered only when there is more than this one element — a panel
          saying "1 element (1 on this page)" is noise. */}
      {siblings && siblings.context_total > 1 && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2 }}
          component="section"
          aria-labelledby="sibling-elements-heading"
        >
          <Typography variant="h6" component="h2" id="sibling-elements-heading">
            {t('findingDetail.siblings.title', 'Other elements this rule fails on')}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {/* The statuses counted are named, not implied. A bare "12" on an
                accessibility product reads as "12 things are broken", so if the
                count ever excluded or included something else the wording has
                to move with it — hence counted_statuses comes from the API. */}
            {/* `total`, not `count`: passing `count` makes i18next resolve a
                plural key (summary_one / summary_other) that does not exist,
                and the string silently fails to render. */}
            {t('findingDetail.siblings.summary', {
              defaultValue:
                'Fails on {{total}} unresolved elements in this tracked place ({{page}} on this page).',
              total: siblings.context_total,
              page: siblings.page_total,
            })}
          </Typography>
          {siblings.items.length > 0 && (
            <List dense sx={{ mt: 1 }}>
              {siblings.items.map((s) => (
                <ListItem key={s.id} disableGutters sx={{ display: 'block' }}>
                  <Link component={RouterLink} to={`/orgs/${slug}/findings/${s.id}`}>
                    {s.element_selector || t('findingDetail.siblings.noSelector', '(no selector)')}
                  </Link>
                  {s.page_url && (
                    <Typography variant="body2" color="text.secondary">
                      {s.page_url}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          )}
          {siblings.has_more && (
            <Typography variant="body2" color="text.secondary">
              {t('findingDetail.siblings.more', 'More elements exist than are listed here.')}
            </Typography>
          )}
        </Paper>
      )}

      {!(def?.remediation || def?.failing_example || def?.passing_example) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" component="h2" color="text.secondary">
            {t('findingDetail.sections.remediation', 'How to fix')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('findingDetail.noRemediation', 'Fix guidance is not yet available for this rule.')}
          </Typography>
          {/* Offered here too — and this is where it earns its keep, since the
              rule itself has no guidance to fall back on. */}
          {orgId && finding && <AiFixSuggestion orgId={orgId} findingId={finding.id} />}
        </Paper>
      )}

      {(def?.remediation || def?.failing_example || def?.passing_example) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" component="h2" color="text.secondary">
            {t('findingDetail.sections.remediation', 'How to fix')}
          </Typography>
          {def.remediation && (
            <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {def.remediation}
            </Typography>
          )}
          {def.failing_example && (
            <>
              <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 2 }}>
                {t('findingDetail.failingExample', 'Failing example')}
              </Typography>
              <CodeBlock code={def.failing_example} borderColor="error.light" />
            </>
          )}
          {def.passing_example && (
            <>
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 2 }}>
                {t('findingDetail.passingExample', 'Passing example')}
              </Typography>
              <CodeBlock code={def.passing_example} borderColor="success.light" />
            </>
          )}
          {/* REV-513: renders nothing unless the org has its own AI key. */}
          {orgId && finding && <AiFixSuggestion orgId={orgId} findingId={finding.id} />}
        </Paper>
      )}

      <Dialog open={fpOpen} onClose={() => setFpOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('findingDetail.fpDialog.title', 'Mark as false positive')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {fpError && <Alert severity="error">{fpError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              {t(
                'findingDetail.fpDialog.help',
                'Use this when the finding itself is wrong. If the issue is real but you are not going to fix it, use “Won’t fix” instead.'
              )}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="fp-reason-label">
                {t('findingDetail.fpDialog.reason', 'Reason')}
              </InputLabel>
              <Select
                labelId="fp-reason-label"
                id="fp-reason"
                value={fpReason}
                label={t('findingDetail.fpDialog.reason', 'Reason')}
                onChange={(e: SelectChangeEvent) =>
                  setFpReason(e.target.value as FalsePositiveReason)
                }
              >
                {FALSE_POSITIVE_REASONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {t(`findingDetail.fpReasons.${r}`, r.replace(/_/g, ' '))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('findingDetail.fpDialog.notes', 'Notes')}
              value={fpNotes}
              onChange={(e) => {
                setFpNotes(e.target.value);
                if (fpError) setFpError(null);
              }}
              required
              error={Boolean(fpError)}
              helperText={t(
                'findingDetail.fpDialog.notesHelp',
                'Required. Explain why this finding is incorrect.'
              )}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFpOpen(false)} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleFpSubmit} disabled={submitting}>
            {submitting
              ? t('common.saving', 'Saving…')
              : t('findingDetail.fpDialog.submit', 'Mark as false positive')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={wfOpen} onClose={() => setWfOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('findingDetail.wfDialog.title', "Mark as won't fix")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {wfError && <Alert severity="error">{wfError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              {t(
                'findingDetail.wfDialog.help',
                'Use this when the finding is correct but will not be fixed. If the finding itself is wrong, use “False positive” instead.'
              )}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="wf-reason-label">
                {t('findingDetail.wfDialog.reason', 'Reason')}
              </InputLabel>
              <Select
                labelId="wf-reason-label"
                id="wf-reason"
                value={wfReason}
                label={t('findingDetail.wfDialog.reason', 'Reason')}
                onChange={(e: SelectChangeEvent) => setWfReason(e.target.value as WontFixReason)}
              >
                {WONT_FIX_REASONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {t(`findingDetail.wfReasons.${r}`, r.replace(/_/g, ' '))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('findingDetail.wfDialog.notes', 'Notes (optional)')}
              value={wfNotes}
              onChange={(e) => setWfNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setWfOpen(false)} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleWontFixSubmit}
            disabled={submitting}
          >
            {submitting
              ? t('common.saving', 'Saving…')
              : t('findingDetail.wfDialog.submit', "Mark as won't fix")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('findingDetail.resolveDialog.title', 'Mark as resolved')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {resolveError && <Alert severity="error">{resolveError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              {t(
                'findingDetail.resolveDialog.help',
                'Mark this finding as fixed. You can optionally note how it was resolved.'
              )}
            </Typography>
            <TextField
              label={t('findingDetail.resolveDialog.notes', 'Notes (optional)')}
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setResolveOpen(false)} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleResolveSubmit}
            disabled={submitting}
          >
            {submitting
              ? t('common.saving', 'Saving…')
              : t('findingDetail.resolveDialog.submit', 'Mark as resolved')}
          </Button>
        </DialogActions>
      </Dialog>

      {orgId && findingId && canTriage && (
        <AddToRemediationPlanDialog
          open={addToPlanOpen}
          onClose={() => setAddToPlanOpen(false)}
          orgId={orgId}
          finding={{
            id: findingId,
            // REV-1163: the plan item's title is plain text, so no code-span backticks.
            title: stripCodeSpans(
              def?.name || finding.element_selector || t('findingDetail.untitled', 'Finding')
            ),
            severity: finding.severity,
            contextId: finding.context_id,
          }}
          onAdded={(planName) =>
            setToast(
              t('findingDetail.addedToPlan', {
                defaultValue: 'Added to "{{plan}}".',
                plan: planName,
              })
            )
          }
        />
      )}
    </Box>
  );
}
